import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AdminGridCallbackEvent, AdminGridConfig, AdminGridDirective } from '../../shared/admin-grid.directive';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';
import { NotificationService } from './notification.service';
import {
  NotificationPayloadModalComponent,
  NotificationPayloadModalData,
} from './notification-payload-modal/notification-payload-modal.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
type NotificationTab = 'process_details' | 'process_console' | 'adhoc_request';
type EmailType = 'TO' | 'CC' | 'BCC';
type LookupType = EmailType | 'TO_NOTIF';

interface RoleOption {
  id: number;
  label: string;
}

interface EmailEntry {
  emailAddress: string;
  status?: string;
  copy_type?: string;
}

interface UserSuggestion {
  email: string;
  name: string;
}

interface ProcessDetail {
  process_id?: string | number;
  process_name?: string;
  description?: string;
  subject?: string;
  template?: string;
  templateId?: string | number;
  templateStatus?: string;
  processStatus?: 'A' | 'N' | string;
  html_ind?: 'Y' | 'N' | string;
  userTypeKey?: string | null;
  addlEmailList?: EmailEntry[] | string;
  created_by?: string;
}

const EMPTY_PROCESS: ProcessDetail = {
  addlEmailList: [],
  created_by: '',
  description: '',
  html_ind: '',
  processStatus: '',
  process_id: '',
  process_name: '',
  subject: '',
  template: '',
  templateId: '',
  templateStatus: '',
  userTypeKey: '',
};

/**
 * Process Console grid identifiers (preserved from the legacy AngularJS controller):
 *   GTW12/ui/app/components/admin/notification/notificationController.js
 *     → GridFactory.getDataCustom(base_url + '/loadGridJson?action_id=31159&grid_id=5331&...')
 */
const NOTIFICATION_CONSOLE_ACTION_ID = 31159;
const NOTIFICATION_CONSOLE_GRID_ID = '5331';

/**
 * Admin → Notification screen.
 *
 * Migrated from the legacy AngularJS controller `NotificationController`
 * (`GTW12/ui/app/components/admin/notification/notificationController.js`)
 * and template `notification-tpl.html` / `notification-tabs-tpl.html`.
 *
 * Three tabs:
 *  1. Process Details   — edit the selected notification process
 *  2. Process Console   — gizmo grid (action_id=31159, grid_id=5331) of past notifications
 *  3. On Demand         — send an ad-hoc notification using a process template
 */
@Component({
    selector: 'gtw-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NotificationComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) consoleGrid?: AdminGridDirective;

  /** Roles available for the multi-select on the Process Details tab. */
  readonly roles: RoleOption[] = [
    { id: 1, label: 'User' },
    { id: 2, label: 'Admin' },
    { id: 3, label: 'Editor' },
    { id: 4, label: 'Developer' },
    { id: 5, label: 'Super Admin' },
  ];
  readonly roleDropdownList = this.roles.map((role) => ({
    ...role,
    showCheckbox: true,
    role: 'checkbox',
  }));

  /** Helper text shown beneath the process name input on new processes. */
  readonly processNameMessage = 'All caps without space';

  // Process list (left rail)
  isProcessListLoading = true;
  searchProcessText = '';
  processList: ProcessDetail[] = [];
  processNames: string[] = [];
  showLeftPanel = true;

  // Selection state
  activeTab: NotificationTab = 'process_details';
  selectedProcessName: string | null = null;
  selectedIndex = -1;
  isLoadingProcessDetails = false;
  isProcessSaving = false;
  /**
   * True while the user is creating a brand-new process (after clicking "Add",
   * until the new process is either saved + reloaded as a selection or the
   * user picks another process from the rail). The Process Details form
   * visibility depends on this flag rather than on `process_name === ''`,
   * because the legacy predicate hid the whole form on the first keystroke.
   */
  isAddingNewProcess = false;

  selectedProcess: ProcessDetail = { ...EMPTY_PROCESS };
  private selectedProcessBackup: ProcessDetail = { ...EMPTY_PROCESS };

  // Process Details form state
  /** Selected role objects used by the AppKit multi-select. */
  selectedRoles: RoleOption[] = [];
  templateBody = '';
  templateViewTab: 'body' | 'preview' | 'full' = 'body';
  emailListTo: EmailEntry[] = [];
  emailListCc: EmailEntry[] = [];
  emailListBcc: EmailEntry[] = [];

  // User typeahead state (Process Details + On Demand)
  emailInputTo = '';
  emailInputCc = '';
  emailInputBcc = '';
  loadingEmailTo = false;
  loadingEmailCc = false;
  loadingEmailBcc = false;
  loadingEmailNotif = false;
  userSuggestionsTo: UserSuggestion[] = [];
  userSuggestionsCc: UserSuggestion[] = [];
  userSuggestionsBcc: UserSuggestion[] = [];
  userSuggestionsNotif: UserSuggestion[] = [];

  // On Demand Notification form state
  notificationTo: EmailEntry[] = [];
  onDemandNotifDetails: any = this.buildEmptyOnDemandDetails();
  isJsonValid = false;
  isJsonValidOnInit = true;
  isSendingNotification = false;

  // Process Console (gizmo grid)
  consoleGridConfig: AdminGridConfig | null = null;
  consoleLoading = false;

  // Reactive lookup streams (per email field) — debounced so we hit /userDetails sparingly.
  private readonly lookupStreams: Record<LookupType, Subject<string>> = {
    TO: new Subject<string>(),
    CC: new Subject<string>(),
    BCC: new Subject<string>(),
    TO_NOTIF: new Subject<string>(),
  };
  private readonly lookupSubs: Subscription[] = [];

  private clientKey: string | number = '';
  private clients: any[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.initClientData();
    this.onDemandNotifDetails.client_key = this.clientKey;
    this.wireUserLookupStreams();
    this.loadProcessList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.lookupSubs.forEach(s => s.unsubscribe());
  }

  // ---------------------------------------------------------------------------
  // Init helpers
  // ---------------------------------------------------------------------------

  private initClientData(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.clients = userSettings.user.clients || [];
      this.clientKey =
        userSettings.user.client?.client_key
        ?? userSettings.user.client_key
        ?? sessionStorage.getItem('client_key')
        ?? '';
    }
  }

  get clientList(): any[] {
    return this.clients;
  }

  private wireUserLookupStreams(): void {
    (Object.keys(this.lookupStreams) as LookupType[]).forEach((type) => {
      const sub = this.lookupStreams[type]
        .pipe(
          debounceTime(350),
          distinctUntilChanged(),
          switchMap((term: string) => {
            const query = String(term || '').trim();
            if (query.length < 3) {
              this.setLookupLoading(type, false);
              this.setUserSuggestions(type, []);
              this.cdr.detectChanges();
              return of(null);
            }
            this.setLookupLoading(type, true);
            this.cdr.detectChanges();
            return this.notificationService.getUserLookup(query).pipe(
              catchError(() => of({ userDetails: [] }))
            );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((response: any) => {
          if (response === null) return;
          const list = Array.isArray(response?.userDetails) ? response.userDetails : [];
          const mapped: UserSuggestion[] = list.map((u: any) => ({
            email: u?.email || '',
            name: `${u?.first_name || ''} ${u?.last_name || ''} (${u?.email || ''})`.trim(),
          }));
          this.setUserSuggestions(type, mapped);
          this.setLookupLoading(type, false);
          this.cdr.detectChanges();
        });
      this.lookupSubs.push(sub);
    });
  }

  // ---------------------------------------------------------------------------
  // Left rail - process list
  // ---------------------------------------------------------------------------

  loadProcessList(): void {
    this.isProcessListLoading = true;
    this.cdr.detectChanges();

    this.notificationService.getProcessList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.isProcessListLoading = false;

          let listSource: any[] = [];
          if (Array.isArray(data)) {
            listSource = data;
          } else if (Array.isArray(data?.data)) {
            listSource = data.data;
          }

          this.processList = listSource as ProcessDetail[];
          this.processNames = listSource
            .map((item: any) => (typeof item === 'string' ? item : item?.process_name))
            .filter((name: any) => !!name);

          // Preserve selection across refresh; otherwise reset
          if (this.selectedProcessName && !this.processNames.includes(this.selectedProcessName)) {
            this.selectedProcessName = null;
            this.selectedProcess = { ...EMPTY_PROCESS };
            this.selectedProcessBackup = { ...EMPTY_PROCESS };
          } else if (!this.selectedProcessName) {
            // Match legacy "ng-class first row active" hint by visually highlighting
            // — but do NOT auto-select details to keep API traffic identical to legacy.
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isProcessListLoading = false;
          this.processList = [];
          this.processNames = [];
          this.snackbar.openSnackBar('Unable to load notification processes', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  get filteredProcessNames(): string[] {
    const term = (this.searchProcessText || '').toLowerCase().trim();
    if (!term) return this.processNames;
    return this.processNames.filter((name: string) => String(name).toLowerCase().includes(term));
  }

  clearSearch(): void {
    this.searchProcessText = '';
    this.cdr.detectChanges();
  }

  toggleLeftPanel(): void {
    this.showLeftPanel = !this.showLeftPanel;
    this.cdr.detectChanges();
    // Fire a resize event AFTER the panel/toggle CSS transition completes
    // (see $panel-transition-duration in the component SCSS) so that
    // gizmoGrid recalculates against the final right-panel width, not against
    // a width captured mid-animation.
    setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
  }

  selectProcess(processName: string, index: number): void {
    this.selectedProcessName = processName;
    this.selectedIndex = index;
    this.isLoadingProcessDetails = true;
    this.isAddingNewProcess = false;
    this.selectedProcessBackup = { ...EMPTY_PROCESS };
    this.templateBody = '';

    // If the user picked a process while sitting on the On Demand tab, the
    // Process Details form would never appear (it is *ngIf'd by activeTab),
    // making the click look like a no-op. Switch to Process Details so the
    // selection has a visible effect. Process Console stays on its own tab
    // because its grid refreshes for the new selection.
    if (this.activeTab === 'adhoc_request') {
      this.activeTab = 'process_details';
    }

    // Build the console grid config eagerly so it is ready when the user
    // switches to the Process Console tab (or reload it if we are already there).
    this.ensureConsoleGridConfig();
    this.cdr.detectChanges();

    this.notificationService.getProcessDetails(processName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProcessDetail) => {
          this.isLoadingProcessDetails = false;
          this.selectedProcess = { ...EMPTY_PROCESS, ...(response || {}) } as ProcessDetail;
          this.selectedProcessBackup = safeJsonInput<any>(JSON.stringify(this.selectedProcess), null, 'notification.parse1');
          this.templateBody = this.extractBody(this.selectedProcess.template || '');
          this.setSelectedRoles();
          this.setEmailListsFromProcess();

          // If we're already on the console tab, the grid is mounted with the
          // previous process's params — force a refresh with the new ones.
          if (this.activeTab === 'process_console') {
            this.refreshConsoleGrid();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoadingProcessDetails = false;
          this.selectedProcess = { ...EMPTY_PROCESS };
          this.selectedProcessBackup = { ...EMPTY_PROCESS };
          this.snackbar.openSnackBar('Unable to load process details', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------------

  setActiveTab(tab: NotificationTab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;

    if (tab === 'process_console' && this.selectedProcessName) {
      this.ensureConsoleGridConfig();
      // First mount: AdminGridDirective.autoLoad fires a fetch automatically.
      // Re-mount or already-initialized: force a fresh fetch with current params.
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.consoleGrid?.isInitialized) {
          this.consoleGrid.refresh({
            client_key: this.clientKey,
            process_name: this.selectedProcessName,
          });
        }
      }, 0);
    }
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Process Details - Roles (AppKit multi-select, mirrors legacy
  // ng-dropdown-multiselect from notification-process-details.html)
  // ---------------------------------------------------------------------------

  onRoleSelectionChange(selected: RoleOption[]): void {
    this.selectedRoles = selected || [];
    this.selectedProcess.userTypeKey = this.selectedRoles
      .map((role: RoleOption) => role.id)
      .join(',');
    this.cdr.detectChanges();
  }

  /**
   * Populate selected roles from the persisted `userTypeKey` after a
   * process has been loaded.
   *
   * The backend stores roles as a comma-separated list of numeric IDs
   * (e.g. "1,3,5"). Older records may also contain role labels (e.g.
   * "User,Editor") because the legacy onItemSelect handler accidentally
   * wrote labels back to user_type_key. We match BOTH shapes here so
   * historical processes still light up the right rows in the UI.
   */
  private setSelectedRoles(): void {
    const key = this.selectedProcess?.userTypeKey;
    if (key === null || key === undefined || key === '') {
      this.selectedRoles = [];
      return;
    }

    const tokens = String(key)
      .split(',')
      .map((v: string) => v.trim())
      .filter((v: string) => v.length > 0);

    this.selectedRoles = this.roleDropdownList
      .filter((r: RoleOption) =>
        tokens.some((t: string) => {
          const asNum = Number(t);
          if (!Number.isNaN(asNum) && asNum === r.id) return true;
          return t.toLowerCase() === r.label.toLowerCase();
        })
      );
  }

  // ---------------------------------------------------------------------------
  // Process Details - Status / Template / Process Name
  // ---------------------------------------------------------------------------

  onChangeProcessName(): void {
    const v = this.selectedProcess?.process_name || '';
    this.selectedProcess.process_name = String(v).toUpperCase().replace(/\s+/g, '');
  }

  onStatusChange(value: 'A' | 'N'): void {
    this.selectedProcess.processStatus = value;
  }

  onTemplateRequiredChange(value: 'Y' | 'N'): void {
    this.selectedProcess.html_ind = value;
    if (value === 'N') {
      this.templateViewTab = 'body';
    }
    this.cdr.detectChanges();
  }

  onTemplateBodyChange(value: string): void {
    this.templateBody = value || '';
    const parts = this.notificationService.getHtmlParts();
    this.selectedProcess.template = parts.htmlBeforeBody + this.templateBody + parts.htmlAfterBody;
  }

  // ---------------------------------------------------------------------------
  // Process Details - Email TO/CC/BCC chips
  // ---------------------------------------------------------------------------

  private setEmailListsFromProcess(): void {
    this.emailListTo = [];
    this.emailListCc = [];
    this.emailListBcc = [];
    const list = this.selectedProcess?.addlEmailList;
    if (!Array.isArray(list)) return;
    list.forEach((user: EmailEntry) => {
      if (user?.copy_type === 'TO') this.emailListTo.push(user);
      else if (user?.copy_type === 'CC') this.emailListCc.push(user);
      else if (user?.copy_type === 'BCC') this.emailListBcc.push(user);
    });
  }

  onLookupChange(type: LookupType, value: string): void {
    if (type === 'TO') this.emailInputTo = value;
    else if (type === 'CC') this.emailInputCc = value;
    else if (type === 'BCC') this.emailInputBcc = value;
    else if (type === 'TO_NOTIF') this.onDemandNotifDetails.email_addr = value;
    this.lookupStreams[type].next(value || '');
  }

  selectUserSuggestion(type: LookupType, user: UserSuggestion): void {
    const email = String(user?.email || '').trim();
    if (!email) return;
    if (type === 'TO_NOTIF') {
      this.onDemandNotifDetails.email_addr = email;
      this.addNotificationEmail();
      this.setUserSuggestions(type, []);
      this.cdr.detectChanges();
      return;
    }

    if (type === 'TO') this.emailInputTo = email;
    if (type === 'CC') this.emailInputCc = email;
    if (type === 'BCC') this.emailInputBcc = email;
    this.addEmail(type);
    this.setUserSuggestions(type, []);
  }

  addEmail(type: EmailType): void {
    const value = (type === 'TO' ? this.emailInputTo : type === 'CC' ? this.emailInputCc : this.emailInputBcc).trim();
    if (!value) return;
    const email: EmailEntry = { emailAddress: value, status: 'A', copy_type: type };
    const complete = [...this.emailListTo, ...this.emailListCc, ...this.emailListBcc];
    if (this.notificationService.isDuplicateEmail(complete, email)) {
      this.snackbar.openSnackBar('Email already added', 'gtw-snackbar--yellow');
      return;
    }
    if (type === 'TO') {
      this.emailListTo = [...this.emailListTo, email];
      this.emailInputTo = '';
    } else if (type === 'CC') {
      this.emailListCc = [...this.emailListCc, email];
      this.emailInputCc = '';
    } else {
      this.emailListBcc = [...this.emailListBcc, email];
      this.emailInputBcc = '';
    }
    // Reset the lookup stream's last-seen value so retyping the same phrase
    // (e.g. searching for the same user twice) is not swallowed by
    // distinctUntilChanged in wireUserLookupStreams.
    this.resetLookupStream(type);
    this.cdr.detectChanges();
  }

  removeEmail(type: EmailType, index: number): void {
    if (type === 'TO') this.emailListTo = this.emailListTo.filter((_e, i) => i !== index);
    if (type === 'CC') this.emailListCc = this.emailListCc.filter((_e, i) => i !== index);
    if (type === 'BCC') this.emailListBcc = this.emailListBcc.filter((_e, i) => i !== index);
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Process Details - Add new / Save / Reset
  // ---------------------------------------------------------------------------

  addNewProcess(): void {
    this.selectedProcessName = null;
    this.selectedIndex = -1;
    this.selectedProcess = { ...EMPTY_PROCESS };
    this.selectedProcessBackup = { ...EMPTY_PROCESS };
    this.selectedRoles = [];
    this.templateBody = '';
    this.templateViewTab = 'body';
    this.emailListTo = [];
    this.emailListCc = [];
    this.emailListBcc = [];
    this.activeTab = 'process_details';
    this.isAddingNewProcess = true;
    // Clear any active rail filter so a newly-saved process whose name does
    // not match the filter still shows up in `filteredProcessNames`.
    this.searchProcessText = '';
    this.cdr.detectChanges();
  }

  saveProcess(): void {
    if (!this.isValidProcess()) {
      this.snackbar.openSnackBar('Process Name is required', 'gtw-snackbar--yellow');
      return;
    }
    this.isProcessSaving = true;
    this.cdr.detectChanges();

    const payload: ProcessDetail = { ...this.selectedProcess };
    payload.addlEmailList = [...this.emailListTo, ...this.emailListCc, ...this.emailListBcc];
    payload.userTypeKey = (this.selectedRoles || []).map((role) => role.id).join(',');

    this.notificationService.saveProcessDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.isProcessSaving = false;
          const isSuccess = !response?.errorMessage
            && (String(response?.responseData) === '1' || String(response?.callSuccess) === '1');
          if (isSuccess) {
            this.snackbar.openSnackBar('Data saved successfully', 'gtw-snackbar--green');
            const processName = String(payload.process_name || '');
            this.loadProcessList();
            // Re-fetch the persisted record so `selectedProcess.process_id`
            // and `selectedProcessBackup` are populated — without this the
            // form stays in "add new" mode after save (Process Name input
            // remains editable, Reset reverts to an empty record).
            if (processName) {
              this.selectProcess(processName, -1);
            } else {
              this.isAddingNewProcess = false;
            }
          } else {
            this.snackbar.openSnackBar(
              response?.errorMessage || 'An error has occurred. If this error continues please contact support.',
              'gtw-snackbar--red',
            );
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.isProcessSaving = false;
          this.snackbar.openSnackBar('An error has occurred while saving. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  resetProcess(): void {
    this.selectedProcess = safeJsonInput<any>(JSON.stringify(this.selectedProcessBackup || EMPTY_PROCESS), null, 'notification.parse2');
    this.templateBody = this.extractBody(this.selectedProcess?.template || '');
    this.setSelectedRoles();
    this.setEmailListsFromProcess();
    this.cdr.detectChanges();
  }

  private isValidProcess(): boolean {
    const name = (this.selectedProcess?.process_name || '').toString().trim();
    return !!name;
  }

  private extractBody(template: string): string {
    if (!template) return '';
    const match = template.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
    return match && match[1] != null ? match[1] : template;
  }

  // ---------------------------------------------------------------------------
  // Process Console (gizmo grid via AdminGridDirective)
  // ---------------------------------------------------------------------------

  private ensureConsoleGridConfig(): void {
    if (!this.selectedProcessName) {
      this.consoleGridConfig = null;
      return;
    }
    // Always create a NEW object reference so Angular's @Input change
    // detection picks it up and pushes the updated extraParams to the
    // AdminGridDirective.
    this.consoleGridConfig = {
      actionId: NOTIFICATION_CONSOLE_ACTION_ID,
      gridId: NOTIFICATION_CONSOLE_GRID_ID,
      extraParams: {
        client_key: this.clientKey,
        process_name: this.selectedProcessName,
      },
      autoLoad: true,
      cacheKey: `admin/notification/console/${this.selectedProcessName}`,
    };
    // Show the overlay BEFORE the grid mounts so the user never sees the
    // bare gizmoGrid scaffold. The directive's loadingStateChange will keep
    // it true during the fetch and set it back to false on completion.
    this.consoleLoading = true;
  }

  refreshConsoleGrid(): void {
    if (!this.selectedProcessName) return;
    this.ensureConsoleGridConfig();
    this.cdr.detectChanges();
    if (this.consoleGrid?.isInitialized) {
      this.consoleGrid.clearCache();
      this.consoleGrid.refresh({
        client_key: this.clientKey,
        process_name: this.selectedProcessName,
      });
    }
  }

  onConsoleGridLoadingChange(loading: boolean): void {
    if (loading) {
      this.consoleLoading = true;
      this.cdr.detectChanges();
      return;
    }
    // gizmoGrid paints rows in a follow-up frame after loadData() returns;
    // delay overlay removal one paint tick (~50ms) so the user never sees
    // the half-painted grid through the (now-removed) overlay.
    setTimeout(() => {
      this.consoleLoading = false;
      this.cdr.detectChanges();
    }, 50);
  }

  onConsoleGridDataLoaded(_data: any): void {
    // loadingStateChange(false) handles overlay removal — we just need a
    // CD tick here so OnPush picks up any other state that may have
    // changed (e.g. cached row counts, etc.).
    this.cdr.detectChanges();
  }

  onConsoleGridCallback(event: AdminGridCallbackEvent): void {
    if (event.callName === 'payloadDetail') {
      const rowData = event.data?.rowData ?? event.data;
      this.openPayloadDetails(rowData);
    }
  }

  private openPayloadDetails(rowData: any): void {
    if (!rowData) return;
    const data: NotificationPayloadModalData = {
      processName: rowData?.PROCESS_NAME || rowData?.process_name || this.selectedProcessName || '',
      notificationId: rowData?.NOTIFY_ID || rowData?.notify_id || '',
      payloadRaw: rowData?.PAYLOAD ?? rowData?.payload ?? '',
    };

    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '70vw';
    config.maxWidth = '900px';
    config.disableClose = true;
    config.data = data;

    this.adminScreenContext.setRowData(rowData);
    const dialogRef = this.dialog.open(NotificationPayloadModalComponent, config);
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.adminScreenContext.setRowData({});
      });
  }

  // ---------------------------------------------------------------------------
  // On Demand Notification
  // ---------------------------------------------------------------------------

  private buildEmptyOnDemandDetails(): any {
    return {
      process_name: '',
      payload: '',
      subject: '',
      client_key: '',
      source_name: 'GTW',
      email_addr: '',
    };
  }

  addNotificationEmail(): void {
    const value = String(this.onDemandNotifDetails?.email_addr || '').trim();
    if (!value) return;
    if (this.notificationService.isDuplicateEmail(this.notificationTo, { emailAddress: value })) {
      this.snackbar.openSnackBar('Email already added', 'gtw-snackbar--yellow');
      return;
    }
    this.notificationTo = [
      ...this.notificationTo,
      { emailAddress: value, status: 'A', copy_type: 'TO_NOTIF' },
    ];
    this.onDemandNotifDetails.email_addr = '';
    this.resetLookupStream('TO_NOTIF');
    this.cdr.detectChanges();
  }

  removeNotificationEmail(index: number): void {
    this.notificationTo = this.notificationTo.filter((_e, i) => i !== index);
    this.cdr.detectChanges();
  }

  updateJsonObject(value: string): void {
    this.isJsonValidOnInit = !value;
    if (!value) {
      this.isJsonValid = false;
      return;
    }
    try {
      safeJsonInput<any>(value, null, 'notification.parse3');
      this.isJsonValid = true;
    } catch {
      this.isJsonValid = false;
    }
  }

  sendNotification(): void {
    if (!this.onDemandNotifDetails.process_name) {
      this.snackbar.openSnackBar('Please choose a process', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.isJsonValid) {
      this.onDemandNotifDetails.payload = this.onDemandNotifDetails.payload || '';
      this.snackbar.openSnackBar('Please enter a valid JSON payload', 'gtw-snackbar--red');
      return;
    }

    this.isSendingNotification = true;
    this.cdr.detectChanges();

    const payload = {
      ...this.onDemandNotifDetails,
      source_name: this.onDemandNotifDetails?.source_name || 'GTW',
      email_addr: this.notificationTo.map((m: EmailEntry) => m.emailAddress).join(','),
      sso: this.appService.USER_SETTINGS?.user?.sso_id || '',
    };

    this.notificationService.sendNotification(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSendingNotification = false;
          this.snackbar.openSnackBar('Notification sent successfully', 'gtw-snackbar--green');
          this.resetNotification();
          this.cdr.detectChanges();
        },
        error: () => {
          this.isSendingNotification = false;
          this.snackbar.openSnackBar('Error occurred while sending notification', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  resetNotification(): void {
    this.isJsonValidOnInit = true;
    this.isJsonValid = false;
    this.onDemandNotifDetails = this.buildEmptyOnDemandDetails();
    this.onDemandNotifDetails.client_key = this.clientKey;
    this.notificationTo = [];
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  private setLookupLoading(type: LookupType, isLoading: boolean): void {
    if (type === 'TO') this.loadingEmailTo = isLoading;
    if (type === 'CC') this.loadingEmailCc = isLoading;
    if (type === 'BCC') this.loadingEmailBcc = isLoading;
    if (type === 'TO_NOTIF') this.loadingEmailNotif = isLoading;
  }

  private setUserSuggestions(type: LookupType, values: UserSuggestion[]): void {
    if (type === 'TO') this.userSuggestionsTo = values;
    if (type === 'CC') this.userSuggestionsCc = values;
    if (type === 'BCC') this.userSuggestionsBcc = values;
    if (type === 'TO_NOTIF') this.userSuggestionsNotif = values;
  }

  /**
   * Push an empty string into the lookup stream so distinctUntilChanged
   * does not silently drop a later re-typing of the same phrase. Used
   * after a chip is added (input cleared without ngModelChange) or after
   * a suggestion is selected.
   */
  private resetLookupStream(type: LookupType): void {
    this.lookupStreams[type].next('');
  }

  trackByName(_index: number, name: string): string {
    return name;
  }

  trackByEmailAddress(_index: number, email: EmailEntry): string {
    return email.emailAddress;
  }
}
